export type MaterialType = 'animal' | 'support';

export type ContactMaterialClass = 'animal-support' | 'animal-animal' | 'other';

export interface MaterialBody {
  label?: string;
  parent?: MaterialBody;
  plugin?: {
    reverseAnimalTower?: {
      materialType?: MaterialType;
    };
    [key: string]: unknown;
  };
}

export interface ContactMaterialTelemetry {
  materialClass: ContactMaterialClass;
  bodyAType: MaterialType | 'other';
  bodyBType: MaterialType | 'other';
  baseFriction: number;
  baseFrictionStatic: number;
  resolvedFriction: number;
  resolvedFrictionStatic: number;
}

export interface ContactMaterialPair {
  friction: number;
  frictionStatic: number;
  collision: {
    parentA: MaterialBody;
    parentB: MaterialBody;
  };
  reverseAnimalTowerMaterial?: ContactMaterialTelemetry;
}

export const ANIMAL_SUPPORT_FRICTION = 1;
export const ANIMAL_SUPPORT_FRICTION_STATIC = 1.8;
export const ANIMAL_SUPPORT_FRICTION_PARAM = 'supportFriction';
export const ANIMAL_SUPPORT_FRICTION_STATIC_PARAM = 'supportFrictionStatic';
export const ANIMAL_ANIMAL_MATERIAL_MULTIPLIER = 0.95;
export const ANIMAL_ANIMAL_MATERIAL_MULTIPLIER_PARAM = 'animalFrictionMultiplier';
const PINNED_MATTER_VERSION = '0.20.0';
const installedMatterNamespaces = new WeakSet<object>();

function materialTypeOf(body: MaterialBody): MaterialType | 'other' {
  return body.plugin?.reverseAnimalTower?.materialType ?? 'other';
}

export function markBodyMaterialType(body: MaterialBody, materialType: MaterialType): void {
  body.plugin ??= {};
  body.plugin.reverseAnimalTower = {
    ...body.plugin.reverseAnimalTower,
    materialType,
  };
}

export function classifyContactMaterial(bodyA: MaterialBody, bodyB: MaterialBody): ContactMaterialClass {
  const typeA = materialTypeOf(bodyA);
  const typeB = materialTypeOf(bodyB);
  if ((typeA === 'animal' && typeB === 'support') || (typeA === 'support' && typeB === 'animal')) {
    return 'animal-support';
  }
  if (typeA === 'animal' && typeB === 'animal') return 'animal-animal';
  return 'other';
}

export function contactMaterialOverrideEnabled(search: string): boolean {
  return new URLSearchParams(search).get('contactMaterial') !== 'baseline';
}

function nonNegativeNumberFromSearch(search: string, parameter: string, fallback: number): number {
  const rawValue = new URLSearchParams(search).get(parameter);
  if (rawValue === null || rawValue.trim() === '') return fallback;
  const value = Number(rawValue);
  return Number.isFinite(value) && value >= 0 ? value : fallback;
}

export function animalSupportFrictionFromSearch(search: string): number {
  return nonNegativeNumberFromSearch(search, ANIMAL_SUPPORT_FRICTION_PARAM, ANIMAL_SUPPORT_FRICTION);
}

export function animalSupportFrictionStaticFromSearch(search: string): number {
  return nonNegativeNumberFromSearch(
    search,
    ANIMAL_SUPPORT_FRICTION_STATIC_PARAM,
    ANIMAL_SUPPORT_FRICTION_STATIC,
  );
}

export function animalAnimalMaterialMultiplierFromSearch(search: string): number {
  return nonNegativeNumberFromSearch(
    search,
    ANIMAL_ANIMAL_MATERIAL_MULTIPLIER_PARAM,
    ANIMAL_ANIMAL_MATERIAL_MULTIPLIER,
  );
}

interface ContactMaterialOptions {
  enabled?: boolean;
  animalAnimalMaterialMultiplier?: number;
  animalSupportFriction?: number;
  animalSupportFrictionStatic?: number;
}

export function applyContactMaterialOverride(
  pair: ContactMaterialPair,
  options: ContactMaterialOptions = {},
): ContactMaterialTelemetry {
  const bodyAType = materialTypeOf(pair.collision.parentA);
  const bodyBType = materialTypeOf(pair.collision.parentB);
  const materialClass = classifyContactMaterial(pair.collision.parentA, pair.collision.parentB);
  const baseFriction = pair.friction;
  const baseFrictionStatic = pair.frictionStatic;
  const configuredAnimalMultiplier = options.animalAnimalMaterialMultiplier;
  const configuredSupportFriction = options.animalSupportFriction;
  const configuredSupportFrictionStatic = options.animalSupportFrictionStatic;

  if (options.enabled !== false && materialClass === 'animal-support') {
    pair.friction = configuredSupportFriction !== undefined
      && Number.isFinite(configuredSupportFriction)
      && configuredSupportFriction >= 0
      ? configuredSupportFriction
      : ANIMAL_SUPPORT_FRICTION;
    pair.frictionStatic = configuredSupportFrictionStatic !== undefined
      && Number.isFinite(configuredSupportFrictionStatic)
      && configuredSupportFrictionStatic >= 0
      ? configuredSupportFrictionStatic
      : ANIMAL_SUPPORT_FRICTION_STATIC;
  } else if (options.enabled !== false && materialClass === 'animal-animal') {
    const multiplier = configuredAnimalMultiplier !== undefined
      && Number.isFinite(configuredAnimalMultiplier)
      && configuredAnimalMultiplier >= 0
      ? configuredAnimalMultiplier
      : ANIMAL_ANIMAL_MATERIAL_MULTIPLIER;
    pair.friction = baseFriction * multiplier;
    pair.frictionStatic = baseFrictionStatic * multiplier;
  }

  const telemetry = {
    materialClass,
    bodyAType,
    bodyBType,
    baseFriction,
    baseFrictionStatic,
    resolvedFriction: pair.friction,
    resolvedFrictionStatic: pair.frictionStatic,
  };
  pair.reverseAnimalTowerMaterial = telemetry;
  return telemetry;
}

export function installContactMaterialOverride(matter: {
  version: string;
  after(path: string, callback: (pair: ContactMaterialPair) => void): void;
}, options: ContactMaterialOptions = {}): void {
  if (matter.version !== PINNED_MATTER_VERSION) {
    throw new Error(`Unsupported Matter.js version ${matter.version}; expected ${PINNED_MATTER_VERSION}`);
  }
  if (installedMatterNamespaces.has(matter)) return;
  matter.after('Pair.update', (pair: ContactMaterialPair) => {
    applyContactMaterialOverride(pair, options);
  });
  installedMatterNamespaces.add(matter);
}
